/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { AnyAction } from 'redux';

import { DEFAULT_RETRY_OPTIONS } from '../../../config';
import { OneLineGuideCell } from '../../../core/entities';
import { Channel } from '../../../core/entities/Channel';
import { GridGuideRow } from '../../../core/entities/GridGuideRow';
import { GuideCell } from '../../../core/entities/GuideCell';
import { GuideType, InteractiveComponents, SeparatorTypes } from '../../../core/enums';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { QoEAppLogger } from '../../../core/logging';
import { VuLogger } from '../../../core/logging/VULogger';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { PlaybackContentType } from '../../../core/playback/enums';
import { PlaybackDataModelImpl } from '../../../core/playback/impls/PlaybackDataModelImpl';
import { RetryMechanism } from '../../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../../core/retrymechanism/RetryOptions';
import { Storage } from '../../../core/storage/Storage';
import { IPlayable } from '../../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { setGridGuideOffsetAction } from '../../guide/guide.actions';
import { loadGridGuide } from '../../guide/guide.reducers';
import { setVerticalPageCount } from '../../guide/guidehighlight/guidehighlight.reducers';
import {
    hideOverlayDialog,
    onKeyLeftOverlayDialog,
    onKeyRightOverlayDialog,
    showOverlayDialog,
} from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController, showGuideScreen } from '../../screens/screens.reducers';
import { startVideoPlayback } from '../../videoplayer/videoplayer.reducers';
import {
    getOneLineGuideChannels,
    refreshOneLineGuideAfterChannelSettingsUpdate,
    showOneLineGuide,
} from '../../videoplayerui/onelineguide/onelineguide.reducers';
import { hideWhisper, showWhisper } from '../../whisper/whisper.reducer';
import { OverlayTypes } from '../OverlayTypes';
import {
    setChannelOptionsAction,
    setChannelOptionsOverlayContextAction,
    setChannelsTypeAction,
} from './channeloptionsoverlay.actions';
import {
    GuideChannelOptionsOverlayActions,
    OneLineGuideChannelOptionsOverlayActions,
} from './channeloptionsoverlay.enum';
import {
    SET_CHANNEL_OPTIONS,
    SET_CHANNEL_OPTIONS_OVERLAY_CONTEXT,
    SET_CHANNELS_TYPE,
} from './channeloptionsoverlay.types';

const initialState: {
    channel: Channel | null;
    channelsTypeIndex: string;
    channelsType: string;
    watchNowText: string;
    channelsText: string;
    guideType: GuideType;
    horizontalSelectionOptions: string[];
} = {
    channel: null,
    channelsTypeIndex:
        Storage.getInstance().getItem(Storage.Keys.CHANNELS_TYPE) ||
        LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS,
    channelsType: '',
    watchNowText: '',
    channelsText: '',
    guideType: GuideType.GRID_GUIDE,
    horizontalSelectionOptions: [],
};
export const GLOBAL_MOUSE_TRAP: string = 'HideChannelOptionsOverlay';

/* TODO FAVORITES:
 *
 *      After favorites feature is fully implemented transform
 *  mHorizontalSelectionOptionIndex back in a plain array.
 *
 *      For the moment we are converting it to a function because
 *  if we leave it as an object, the Favorites option will never
 *  be visible because the object is instantiated only once in the
 *  global scope when the CCBSwitch class is not yet initiated.
 *
 *      Because it is not initiated, it will return false for
 *  any CCB Switch flag.
 *
 *  */
const mHorizontalSelectionOptionIndex = () => [
    LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS,
    LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES,
    LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS,
];

const channelOptionsOverlayReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case SET_CHANNELS_TYPE:
            return {
                ...state,
                channelsType: payload.channelsType,
                channelsTypeIndex: payload.channelsTypeIndex,
            };
        case SET_CHANNEL_OPTIONS:
            return {
                ...state,
                channelsType: payload.channelsType,
                watchNowText: payload.watchNowText,
                channelsText: payload.channelsText,
                horizontalSelectionOptions: payload.horizontalSelectionOptions,
            };
        case SET_CHANNEL_OPTIONS_OVERLAY_CONTEXT:
            return {
                ...state,
                channel: payload.channel,
                guideType: payload.guideType,
            };
        default:
            return state;
    }
};

export const showChannelOptionsOverlay =
    (channel: Channel, guideType: GuideType) => (dispatch: Function, getState: Function) => {
        const { channelOptionsOverlay } = getState();
        const { channelsTypeIndex, watchNowText, channelsText, horizontalSelectionOptions } =
            channelOptionsOverlay;

        const selectedChannelsTypeIndex =
            mHorizontalSelectionOptionIndex().indexOf(channelsTypeIndex);

        const { channelNumber, affiliate, callSign, channelLogoUrl, isFavorite } = channel;
        const addFavoriteChannelsText = Locale.getString(
            LocaleKeys.OVERLAY_OPTIONS_ADD_FAVORITE_CHANNELS_TEXT,
        );
        const removeFavoriteChannelsText = Locale.getString(
            LocaleKeys.OVERLAY_OPTIONS_REMOVE_FAVORITE_CHANNELS_TEXT,
        );

        const favoriteChannelsText = isFavorite
            ? removeFavoriteChannelsText
            : addFavoriteChannelsText;

        const favoriteChannelsOption = {
            text: favoriteChannelsText,
        };

        const caretIndex = guideType === GuideType.ONE_LINE_GUIDE ? 3 : 2;

        const overlayDialogProps: IOverlayDialog = {
            channel,
            enableCaret: true,
            hasDimmer: true,
            hasSeperator: false,
            iconURL: channelLogoUrl,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: [
                {
                    text: watchNowText,
                },
                ...(guideType === GuideType.ONE_LINE_GUIDE
                    ? [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTIONS_GO_TO_GUIDE_TEXT) }]
                    : []),
                favoriteChannelsOption,
                {
                    text: channelsText,
                    horizontalSelectionoptions: horizontalSelectionOptions,
                    hrTop: true,
                    initialHorizontalIndex: selectedChannelsTypeIndex,
                    separatorType: SeparatorTypes.DEFAULT,
                },
            ],
            overlayType: OverlayTypes.CHANNEL_OPTIONS,
            subtitle: `${channelNumber} ${affiliate || callSign}`,
            title: Locale.getString(LocaleKeys.GUIDE_CHANNEL_OPTIONS_OVERLAY_TITLE),
            verticalPositionCaretAvailable: [caretIndex],
        };

        dispatch(setChannelOptionsOverlayContextAction(channel, guideType));
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY));
        dispatch(showOverlayDialog(overlayDialogProps));
    };

export const showGuideOptionsOverlay =
    (guideType: GuideType) => (dispatch: Function, getState: Function) => {
        const { channelsTypeIndex, channelsText, horizontalSelectionOptions } =
            getState().channelOptionsOverlay;

        const selectedChannelsTypeIndex =
            mHorizontalSelectionOptionIndex().indexOf(channelsTypeIndex);
        const overlayDialogProps: IOverlayDialog = {
            enableCaret: true,
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: [
                ...(guideType === GuideType.ONE_LINE_GUIDE
                    ? [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTIONS_GO_TO_GUIDE_TEXT) }]
                    : []),
                {
                    text: channelsText,
                    horizontalSelectionoptions: horizontalSelectionOptions,
                    hrTop: true,
                    initialHorizontalIndex: selectedChannelsTypeIndex,
                },
            ],
            overlayType: OverlayTypes.CHANNEL_OPTIONS,
            title: Locale.getString(LocaleKeys.GUIDE_OPTIONS_OVERLAY_TITLE),
            verticalPositionCaretAvailable: guideType === GuideType.ONE_LINE_GUIDE ? [1] : [0],
        };

        dispatch(setScreenTaskController(ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY));
        dispatch(showOverlayDialog(overlayDialogProps));
    };

export const onKeyOkChannelOptionsOverlayGuide = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition: overlayHighlightVerticalPosition } = getState().overlayHighlight;

    if (overlayHighlightVerticalPosition === GuideChannelOptionsOverlayActions.WATCH_NOW) {
        dispatch(channelOptionsOverlayWatchNow());
    } else if (
        overlayHighlightVerticalPosition ===
        GuideChannelOptionsOverlayActions.FAVORITE_CHANNEL_ADD_REMOVE
    ) {
        dispatch(channelOptionsOverlayAddRemoveFavoriteChannel(true));
    } else {
        dispatch(channelOptionsOverlayApplyFilter());
    }
};

export const onKeyOkChannelOptionsOverlayOneLineGuide =
    () => (dispatch: Function, getState: Function) => {
        const { verticalPosition: overlayHighlightVerticalPosition } = getState().overlayHighlight;

        if (
            overlayHighlightVerticalPosition === OneLineGuideChannelOptionsOverlayActions.WATCH_NOW
        ) {
            dispatch(channelOptionsOverlayWatchNow(true));
        } else if (
            overlayHighlightVerticalPosition ===
            OneLineGuideChannelOptionsOverlayActions.GO_TO_GRID_GUIDE
        ) {
            dispatch(showGuideScreen());
        } else if (
            overlayHighlightVerticalPosition ===
            OneLineGuideChannelOptionsOverlayActions.FAVORITE_CHANNEL_ADD_REMOVE
        ) {
            dispatch(channelOptionsOverlayAddRemoveFavoriteChannel(false));
            dispatch(showOneLineGuide());
        } else {
            dispatch(channelOptionsOverlayApplyFilter());
            dispatch(showOneLineGuide());
        }
    };

export const onKeyPlayChannelOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition: overlayHighlightVerticalPosition } = getState().overlayHighlight;
    if (overlayHighlightVerticalPosition > 0) return;
    dispatch(onKeyOkChannelOptionsOverlayGuide());
};

export const onKeyBackChannelOptionsOverlay = () => (dispatch: Function) => {
    VuLogger.logChannelOptionOverlayCancelUserAction();
    dispatch(hideOverlayDialog());
};

export const setChannelOptions = () => (dispatch: Function) => {
    const watchNowText = Locale.getString(LocaleKeys.OVERLAY_OPTIONS_WATCH_NOW_TEXT);
    const channelsText = Locale.getString(LocaleKeys.OVERLAY_OPTIONS_CHANNELS_TEXT);

    const horizontalSelectionOptions = mHorizontalSelectionOptionIndex().map(Locale.getString);
    const mChannelsType =
        Locale.getString(Storage.getInstance().getItem(Storage.Keys.CHANNELS_TYPE)) ||
        Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS);

    dispatch(
        setChannelOptionsAction(
            mChannelsType,
            watchNowText,
            channelsText,
            horizontalSelectionOptions,
        ),
    );
};

export const onMouseClickChannelOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { activeComponent } = getState().interactiveLayer;
    const { value } = getState().inputFactory;
    const { guideType } = getState().channelOptionsOverlay;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        return;
    }

    if (activeComponent === InteractiveComponents.WEST) dispatch(onKeyLeftOverlayDialog());
    else if (activeComponent === InteractiveComponents.EAST) dispatch(onKeyRightOverlayDialog());
    else if (guideType === GuideType.ONE_LINE_GUIDE)
        dispatch(onKeyOkChannelOptionsOverlayOneLineGuide());
    else dispatch(onKeyOkChannelOptionsOverlayGuide());
};

const getGuideCells = (getState: Function): GuideCell[] => {
    const { guideType } = getState().channelOptionsOverlay;
    let guideCells: GuideCell[] = [];
    if (guideType === GuideType.GRID_GUIDE) {
        const { gridGuide, guideHighlight } = getState();
        const { verticalPosition } = guideHighlight;
        const { guideRows, isPastGuideMode, futureGuideRows } = gridGuide;
        const guideRowsData: GridGuideRow[] = isPastGuideMode ? futureGuideRows : guideRows;
        guideCells = guideRowsData[verticalPosition]?.guideCells;
    }

    if (guideType === GuideType.ONE_LINE_GUIDE) {
        const { cellList } = getState().oneLineGuide;
        guideCells = cellList.map(
            (cell: OneLineGuideCell): GuideCell => ({
                ...cell,
                startTime: new Date(cell.startTime.getTime()),
            }),
        );
    }

    return guideCells;
};

const channelOptionsOverlayWatchNow =
    (isOLG: boolean = false) =>
    (dispatch: Function, getState: Function) => {
        const { channel } = getState().channelOptionsOverlay;

        if (channel) {
            const guideCells = getGuideCells(getState);
            const services = new ServicesClientImpl();
            services.getVuLoggerProperties(channel, guideCells).then((properties) => {
                VuLogger.logItemSelectedEvent(properties);
            });

            if (channel.channelNumber !== QoEAppLogger.lastTunedChannel) {
                QoEAppLogger.setWatchContentEndReason(
                    QoEAppLogger.PlaybackEndReason
                        .USER_MOVE_TO_LIVE_TV_FROM_GUIDE_AND_CHANNEL_WAS_CHANGED,
                );
            }
            if (
                channel.channelNumber !==
                VideoPlaybackController.getInstance().getPlayable()?.channel?.channelNumber
            ) {
                VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
            }
            dispatch(hideOverlayDialog());
            const playbackModelImpl = PlaybackDataModelImpl.getInstance();
            if (!VideoPlaybackController.getInstance().isLive()) {
                VideoPlaybackController.getInstance().updateLastViewedPosition();
                playbackModelImpl.resetPlaybackData();
            }

            const livePlayable: IPlayable = {
                channel,
                playbackContentType: PlaybackContentType.LIVE,
            };

            if (isOLG) {
                dispatch(
                    setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS),
                );
                VideoPlaybackController.getInstance().startPlayback(livePlayable);
            } else dispatch(startVideoPlayback(livePlayable));
        }
    };

export const channelOptionsOverlayAddRemoveFavoriteChannel =
    (isActionFromGuide: boolean) => (dispatch: Function, getState: Function) => {
        const { channel, channelsTypeIndex } = getState().channelOptionsOverlay;
        const { selectedChannel } = getState().channelOptionsOverlay;

        if (channel) {
            const services: ServicesClientImpl = new ServicesClientImpl();
            const setChannelSettings = services.setChannelSettings.bind(services);

            dispatch(hideWhisper());

            RetryMechanism.callServiceWithRetry<boolean>(
                setChannelSettings,
                [channel, !channel.isFavorite],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            ).then((response: boolean) => {
                if (!response) {
                    dispatch(
                        showWhisper({
                            message: Locale.getString(
                                LocaleKeys.CHANNEL_SETTINGS_ERROR_WHISPER_MESSAGE,
                            ),
                            iconType: WhisperTypes.INFO,
                        }),
                    );
                    return;
                }

                dispatch(displayWhisperForActionSuccess());

                services.updateFavoriteChannel(channel, !channel.isFavorite).then(() => {
                    channel.isFavorite = !channel.isFavorite;

                    if (selectedChannel && selectedChannel.channelId === channel.channelId) {
                        selectedChannel.isFavorite = channel.isFavorite;
                    }

                    if (channelsTypeIndex !== LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES) return;

                    if (isActionFromGuide) dispatch(loadGridGuide());
                    else
                        dispatch(refreshOneLineGuideAfterChannelSettingsUpdate(channel.isFavorite));
                });
            });
        }

        dispatch(hideOverlayDialog());
    };

const displayWhisperForActionSuccess = () => (dispatch: Function, getState: Function) => {
    const { channel } = getState().channelOptionsOverlay;
    const channelNameShort = 'CHANNEL_SHORT_NAME';

    const channelText = `${channel.channelNumber} (${channel.callSign})`;
    const whisperMessage = channel.isFavorite
        ? Locale.getString(LocaleKeys.CHANNEL_SETTINGS_REMOVE_WHISPER_MESSAGE).replace(
              channelNameShort,
              channelText,
          )
        : Locale.getString(LocaleKeys.CHANNEL_SETTINGS_ADD_WHISPER_MESSAGE).replace(
              channelNameShort,
              channelText,
          );

    dispatch(
        showWhisper({
            message: whisperMessage,
            iconType: WhisperTypes.INFO,
        }),
    );
};

export const channelOptionsOverlayApplyFilter = () => (dispatch: Function, getState: Function) => {
    const {
        verticalPosition: overlayHighlightVerticalPosition,
        horizontalPosition: overlayHighlightHorizontalPosition,
    } = getState().overlayHighlight;

    const { channel, channelsTypeIndex, guideType } = getState().channelOptionsOverlay;

    const selectedChannelsTypeIndex =
        mHorizontalSelectionOptionIndex()[
            overlayHighlightHorizontalPosition[overlayHighlightVerticalPosition]
        ];

    if (channelsTypeIndex === selectedChannelsTypeIndex) {
        dispatch(hideOverlayDialog());
        return;
    }

    VuLogger.logChannelOptionOverlayOkUserAction(selectedChannelsTypeIndex);
    Storage.getInstance().setItem(Storage.Keys.CHANNELS_TYPE, selectedChannelsTypeIndex);
    dispatch(
        setChannelsTypeAction(
            Locale.getString(LocaleKeys[selectedChannelsTypeIndex]),
            selectedChannelsTypeIndex,
        ),
    );

    if (guideType === GuideType.GRID_GUIDE) {
        dispatch(setVerticalPageCount(0));
        dispatch(setGridGuideOffsetAction(0));
        dispatch(loadGridGuide());
    }
    dispatch(getOneLineGuideChannels(channel, true));
    dispatch(hideOverlayDialog());
};

export default channelOptionsOverlayReducer;
